import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { toast } from '@/design-system/toaster';
import { Invoice, invoicesAPI } from '@/services/api/invoices';

interface HandoverDownloadModalProps {
  isOpen: boolean;
  invoices: Invoice[];
  onClose: () => void;
}

const HandoverDownloadModal: React.FC<HandoverDownloadModalProps> = ({
  isOpen,
  invoices,
  onClose,
}) => {
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setDownloading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleDownload = async () => {
    if (invoices.length === 0) {
      onClose();
      return;
    }
    setDownloading(true);
    try {
      await invoicesAPI.export({
        format: 'xlsx',
        invoiceNumbers: invoices.map((inv) => inv.invoiceNumber),
        status: ['WAITING_CONFIRMATION', 'DISPUTED', 'RESOLVED'],
      });
      onClose();
    } catch (err) {
      toast.error('Handover export failed', {
        description:
          err instanceof Error
            ? err.message
            : 'Could not download the handover spreadsheet. Try the regular export.',
      });
      setDownloading(false);
    }
  };

  const count = invoices.length;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-file-excel-2-line" />}
      iconTone="primary"
      title="Download handover spreadsheet?"
      subtitle={`${count} invoice${count === 1 ? '' : 's'} submitted to the client.`}
      size="sm"
      closeOnBackdropClick={!downloading}
      closeOnEscape={!downloading}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={downloading}
          >
            Skip
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading}
            loading={downloading}
          >
            {!downloading && (
              <i className="ri-download-line mr-1.5 text-[14px]" />
            )}
            {downloading ? 'Preparing…' : 'Download XLSX'}
          </Button>
        </div>
      }
    >
      <p className="text-sm text-text-sub-600">
        Download an XLSX handover slip for the courier — same columns as the
        recon export.
      </p>
    </FormModal>
  );
};

export default HandoverDownloadModal;
